import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Beaker, Snowflake, Truck, FileCheck, BellRing, ShoppingCart, ShieldCheck, Flame, Clock, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import ProductVial from "./ProductVial";
import LiveStockBadge from "./LiveStockBadge";
import useLiveStock from "../lib/useLiveStock";
import { useBactPrompt } from "../lib/bactPrompt";
import { B3G1_SKUS } from "../lib/promo";

export default function ProductCard({ product, featured = false }) {
  const { promptAndAdd } = useBactPrompt();
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySent, setNotifySent] = useState(null); // email we just confirmed
  const liveStock = useLiveStock();
  const live = liveStock[product.sku];
  const qty = live?.stock_quantity ?? product.stock_quantity;
  const status = live?.stock_status ?? product.stock_status;
  const inStock = status === "in_stock" || status === "low_stock";
  const isLowStock = inStock && qty > 0 && qty <= 10;
  const isBestSeller = !!product.best_seller;

  const handleAdd = () => {
    promptAndAdd(product, 1);
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    try {
      await api.post("/notify", { product_id: product.id, email: notifyEmail });
      toast.success("We'll notify you when stock is available");
      setNotifySent(notifyEmail);
      setNotifyOpen(false);
      setNotifyEmail("");
    } catch {
      toast.error("Could not save your request");
    }
  };

  return (
    <article
      className={`pg-card flex flex-col overflow-hidden relative ${featured ? "lg:flex-row" : ""} ${isBestSeller ? "pg-best-seller" : ""}`}
      data-testid={`product-card-${product.sku}`}
    >
      {isBestSeller && (
        <div
          className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center gap-2 py-1.5"
          style={{ background: "linear-gradient(90deg, #A6FF00 0%, #5EEAD4 50%, #00C2D6 100%)" }}
          data-testid={`best-seller-ribbon-${product.sku}`}
        >
          <Flame size={12} className="text-[#0B1220]" />
          <span className="pg-mono text-[10px] uppercase tracking-[0.28em] text-[#0B1220] font-extrabold">
            Peptigen Labs Best Seller
          </span>
          <TrendingUp size={12} className="text-[#0B1220]" />
        </div>
      )}
      <Link
        to={`/products/${product.sku}`}
        className={`relative block ${featured ? "lg:w-1/2 aspect-square lg:aspect-auto" : "aspect-[4/3]"} group ${isBestSeller ? "pt-7" : ""}`}
        data-testid={`product-image-link-${product.sku}`}
      >
        <ProductVial product={product} size={featured ? "lg" : "md"} outOfStock={!inStock} />
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none">
          <div className="flex flex-col gap-1.5">
            <span className="pg-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA8B8] bg-[#0B1220]/80 px-2 py-1 backdrop-blur-sm rounded-sm">
              {product.category.replace(" Research", "").replace(" & ", " · ").slice(0, 28)}
            </span>
            {product.on_sale && (
              <span
                className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm font-bold"
                style={{ background: "#A6FF00", color: "#0B1220" }}
                data-testid={`sale-badge-${product.sku}`}
              >
                Special Offer
              </span>
            )}
            {B3G1_SKUS.includes(product.sku) && (
              <span
                className="pg-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm font-bold"
                style={{ background: "#00C2D6", color: "#0B1220" }}
                data-testid={`b3g1-badge-${product.sku}`}
              >
                Buy 3 Get 1 Free
              </span>
            )}
          </div>
          {inStock ? (
            <span className="pg-badge-stock pg-pulse-glow" data-testid={`badge-instock-${product.sku}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#A6FF00]" />
              In Stock
            </span>
          ) : (
            <span className="pg-badge-out" data-testid={`badge-outofstock-${product.sku}`}>
              Out of Stock
            </span>
          )}
        </div>
        <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end pointer-events-none gap-2">
          {inStock && (
            <span className="pg-mono text-[9px] uppercase tracking-[0.2em] bg-[#0B1220]/85 text-[#5EEAD4] px-2 py-1 border border-[#5EEAD4]/30 rounded-sm">
              {product.purity || "≥99% Purity"}
            </span>
          )}
          {inStock && (
            <span className="pg-mono text-[8.5px] uppercase tracking-[0.18em] bg-[#0B1220]/85 text-[#9CA8B8] px-2 py-1 rounded-sm border border-[#00C2D6]/15">
              Image illustrative
            </span>
          )}
        </div>
      </Link>

      <div className={`p-6 flex flex-col flex-1 ${featured ? "lg:p-8" : ""}`}>
        <Link to={`/products/${product.sku}`} className="block">
          <h3
            className={`text-[#F7FAFC] mb-1 ${featured ? "text-3xl" : "text-xl"}`}
            style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}
          >
            {product.name}
          </h3>
          <div className="text-[#5EEAD4] text-sm pg-mono uppercase tracking-[0.18em] mb-4">
            {product.strength}
          </div>
        </Link>

        <div className="grid grid-cols-2 gap-2 text-[11px] text-[#9CA8B8] mb-5">
          <div className="flex items-center gap-1.5">
            <Beaker size={12} className="text-[#00C2D6]" />
            <span>{product.form === "Sterile solution" ? "Sterile solution" : "Lyophilised powder"}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-[#5EEAD4]" />
            <span>Purity tested</span>
          </div>
          {inStock && (
            <>
              <div className="flex items-center gap-1.5">
                <Truck size={12} className="text-[#A6FF00]" />
                <span>Same-Day Dispatch</span>
              </div>
              <div className="flex items-center gap-1.5">
                <FileCheck size={12} className="text-[#00C2D6]" />
                <span>UK Stock</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-1.5">
            <Snowflake size={12} className="text-[#00C2D6]" />
            <span>Store {product.storage}</span>
          </div>
        </div>

        <div className="flex items-end justify-between mb-5 mt-auto">
          <div>
            <div className="pg-micro mb-1">{product.on_sale ? "Special Offer" : "Research Use Only"}</div>
            <div className="flex items-baseline gap-2">
              <div
                className={`text-2xl ${product.on_sale ? "text-[#A6FF00]" : "text-[#F7FAFC]"}`}
                style={{ fontFamily: "'Exo 2', sans-serif", fontWeight: 700 }}
              >
                £{product.price.toFixed(2)}
              </div>
              {product.compare_at_price && product.on_sale && (
                <div className="text-[#9CA8B8] text-sm line-through pg-mono">
                  £{product.compare_at_price.toFixed(2)}
                </div>
              )}
            </div>
            {product.price_tiers && product.price_tiers.length > 0 && (
              <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#A6FF00] mt-1" data-testid={`tier-hint-${product.sku}`}>
                From £{Math.min(...product.price_tiers.map(t => Number(t.unit_price))).toFixed(2)}/vial in bulk
              </div>
            )}
          </div>
          {product.on_sale && product.compare_at_price && (
            <div className="text-right">
              <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#A6FF00]">Save</div>
              <div className="text-[#A6FF00] pg-display text-lg" style={{ fontFamily: "'Exo 2'" }}>
                £{(product.compare_at_price - product.price).toFixed(2)}
              </div>
            </div>
          )}
        </div>

        {inStock ? (
          <div className="flex flex-col gap-2">
            <div className="flex justify-center">
              <LiveStockBadge qty={qty} status={status} testid={`live-stock-${product.sku}`} />
            </div>
            {isLowStock && (
              <div
                className="flex items-center justify-center gap-2 px-3 py-2 border rounded-sm"
                style={{ background: "rgba(255,107,59,0.08)", borderColor: "rgba(255,107,59,0.4)" }}
                data-testid={`low-stock-urgency-${product.sku}`}
              >
                <Flame size={13} className="text-[#FF6B3B] pg-pulse-glow" />
                <span className="pg-mono text-[10.5px] uppercase tracking-[0.2em] font-bold" style={{ color: "#FF8A5B" }}>
                  Only {qty} left — selling fast
                </span>
              </div>
            )}
            {inStock && !isLowStock && (
              <div className="flex items-center justify-center gap-2 text-[10.5px] pg-mono uppercase tracking-[0.2em] text-[#A6FF00]">
                <Clock size={11} /> Order before 14:00 for next-day UK delivery
              </div>
            )}
            <Link
              to={`/products/${product.sku}`}
              className="pg-btn-ghost text-center"
              data-testid={`view-details-${product.sku}`}
            >
              View Details
            </Link>
            <button
              onClick={handleAdd}
              className="pg-btn-primary"
              data-testid={`add-to-basket-${product.sku}`}
            >
              <ShoppingCart size={16} />
              {isLowStock ? "Buy Now" : "Add to Basket"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="text-[11px] text-[#9CA8B8] pg-mono uppercase tracking-[0.18em] text-center py-2 border border-dashed border-[#9CA8B8]/30">
              Out of stock at the minute
            </div>
            <button
              disabled
              className="pg-btn-primary"
              data-testid={`unavailable-${product.sku}`}
            >
              Unavailable
            </button>
            {notifyOpen ? (
              <form onSubmit={handleNotify} className="flex gap-2">
                <input
                  type="email"
                  required
                  placeholder="your@lab.ac.uk"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="pg-input flex-1 text-xs"
                  data-testid={`notify-email-${product.sku}`}
                />
                <button type="submit" className="pg-btn-ghost text-xs px-3" data-testid={`notify-submit-${product.sku}`}>
                  Save
                </button>
              </form>
            ) : notifySent ? (
              <div
                className="text-[11px] p-2 rounded-sm border border-[#F7B500]/30 bg-[#F7B500]/10 text-[#F7B500] leading-snug"
                data-testid={`notify-sent-${product.sku}`}
              >
                <div className="text-[#F7FAFC]">We'll email <span className="text-[#5EEAD4]">{notifySent}</span> when it's back.</div>
                <div className="text-[#9CA8B8] mt-0.5">Confirmation on its way — check your <strong>Spam/Junk</strong> folder if it's not in your inbox.</div>
              </div>
            ) : (
              <button
                onClick={() => setNotifyOpen(true)}
                className="pg-btn-ghost"
                data-testid={`notify-me-${product.sku}`}
              >
                <BellRing size={14} /> Notify Me
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
